import { useCallback, useEffect, useMemo, useState } from 'react';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';

import { DerivativesOrderProjection, UpdateDerivativesOrderRequest } from 'common/api/autogenerated/trading-gateway';

import { useBowser } from 'common/hooks/bowser/useBowser';
import { useI18n } from 'common/hooks/useI18n/useI18n.hook';
import { usePopup } from 'common/hooks/usePopup';
import { EditDerivativeOrderPopup } from 'common/popups';

import {
  DerivativeOrderFormMode,
  DerivativeOrderFormType,
} from 'views/derivatives/dashboard/components/OrderForm/types';
import {
  actionGetDerivativesOrders,
  actionSuccessDeleteDerivativeOrder,
} from 'views/derivatives/redux/derivatives.actions';
import { deleteDerivativeOrder, editDerivativeOrder } from 'views/derivatives/redux/derivatives.api';

export const useDerivativesOrders = (
  filterFn?: (order: DerivativesOrderProjection) => boolean,
  waitFn = false,
  useCached = false,
) => {
  const dispatch = useDispatch();
  const { show } = usePopup();
  const { isMobile } = useBowser();
  const { translate } = useI18n();
  const [initiallyWasLoaded, setInitiallyWasLoaded] = useState(false);

  const { isAuthenticated } = useSelector((state: DefaultRootState) => state.common.user);

  const loadingReducer = useSelector((state: DefaultRootState) => state.derivativesReducer.loading.ordersInitial);
  const loading = useMemo(() => (isAuthenticated ? loadingReducer : false), [isAuthenticated, loadingReducer]);

  const orders = (useSelector((state: DefaultRootState) => state.derivativesReducer.orders) || []).filter((order) => {
    if (!filterFn) return true;
    return filterFn(order);
  });

  // const cancel = useCallback((id: number, symbol?: string) => dispatch(xxx(id, symbol)), [dispatch]);

  const reload = useCallback(() => dispatch(actionGetDerivativesOrders()), [dispatch]);
  const onDelete = useCallback(
    (id: DerivativesOrderProjection['id']) =>
      deleteDerivativeOrder(id).then(() => {
        dispatch(actionSuccessDeleteDerivativeOrder());
      }),
    [dispatch],
  );
  const onEdit = useCallback(
    (id: DerivativesOrderProjection['id'], order: UpdateDerivativesOrderRequest) => editDerivativeOrder(id, order),
    [],
  );

  const editPopupTitle = useCallback(
    (popupMode: DerivativeOrderFormMode) => {
      if (isMobile) return undefined;

      switch (popupMode) {
        case 'delete':
          return translate.toText('mobile:derivative:popup:delete:title');

        default:
          return translate.toText('mobile:derivative:popup:edit:title');
      }
    },
    [isMobile, translate],
  );

  const onModify = useCallback(
    (
      order: DerivativesOrderProjection,
      popupMode: DerivativeOrderFormMode,
      update?: Partial<DerivativeOrderFormType>,
    ) =>
      show(
        EditDerivativeOrderPopup({
          title: editPopupTitle(popupMode),
          order,
          update,
          hideCloseIcon: isMobile,
          animation: isMobile ? 'slideLeft' : 'appearUp',
          mode: popupMode,
        }),
      ),
    [editPopupTitle, isMobile, show],
  );

  const userNotAuthenticated = useMemo(() => !isAuthenticated, [isAuthenticated]);
  const isWaitingToProvideFilterFn = useMemo(() => waitFn && !filterFn, [filterFn, waitFn]);
  const existsCached = useMemo(() => useCached && !!orders?.length, [orders?.length, useCached]);

  useEffect(() => {
    if (userNotAuthenticated || isWaitingToProvideFilterFn || initiallyWasLoaded || existsCached) return;
    dispatch(actionGetDerivativesOrders());
    setInitiallyWasLoaded(true);
  }, [dispatch, existsCached, initiallyWasLoaded, isWaitingToProvideFilterFn, userNotAuthenticated]);

  return { orders, loading, reload, onDelete, onEdit, onModify };
};
